import React, { useState } from "react";
import EditEbookIndex from "./EditEbook/index";
import useEbook from "../useEbook";
import EditImg from "../../../images/SideBar/Edit";
import Loader from "../../../components/Loader";
import CommonDataGrid from "../../../components/CommonDataGrid";
import Button from "../../../components/Botton";
import { Toaster } from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import { TextField } from "@mui/material";
import DeleteImg from "../../../images/SideBar/Delete";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { getSortByTimeButtonText } from "../../../util/getSortByTimeText";
import { hasPermission } from "../../../utils/permissions";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/slices/authSlice";
import { getDeleteConfirmationMessage } from "../../../utils/pagePermissions";

const Ebook: React.FC = () => {
  const user = useAppSelector(selectUser);
  const canEdit = hasPermission(user, "ebook", "update");
  const canAdd = hasPermission(user, "ebook", "create");
  const canDelete = hasPermission(user, "ebook", "delete");

  const columns: GridColDef[] = [
    {
      field: "bookName",
      headerName: "Ebook Name",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "languageName",
      headerName: "Language",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "standard",
      headerName: "Education",
      align: "left",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "courseName",
      headerName: "Stream",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "subjectName",
      headerName: "Subject",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "eBookPrice",
      headerName: "E-Price",
      align: "center",
      headerAlign: "center",
      flex: 1,
      type: "number",
      filterable: true,
    },
    {
      field: "isPhysicalBookAvailable",
      headerName: "P-Book",
      align: "center",
      headerAlign: "center",
      flex: 1,
      type: "singleSelect",
      valueOptions: ['Available', 'NA'],
      filterable: true,
    },
    {
      field: "physicalBookPrice",
      headerName: "P-Price",
      align: "center",
      headerAlign: "center",
      flex: 1,
      type: "number",
      filterable: true,
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: "Actions",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="flex h-full items-center justify-center gap-3">
          {canEdit && (
            <button
              onClick={() => handleEdit(params.row._id)}
              className="text-fontColor hover:text-primary-2"
            >
              <EditImg />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => openDeleteModal(params.row._id)}
              className="text-fontColor hover:text-red-500"
            >
              <DeleteImg />
            </button>
          )}
        </div>
      ),
    });
  }

  const {
    selectedEbookId,
    setSelectedEbookId,
    ebookData,
    loading,
    searchQuery,
    setSearchQuery,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    setFilterModel,
    setSortingModel,
    handleAdd,
    rowCount,
    paginationModel,
    setPaginationModel,
    // New time sort items
    handleSortByTime,
    timeSortOrder,
  } = useEbook(columns);

  const [isModalOpen, setIsModalOpen] = useState(false); // Modal open state
  const [ebookToDelete, setEbookToDelete] = useState<string | null>(null);

  const openDeleteModal = (id: string) => {
    setEbookToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (ebookToDelete) {
      handleDelete(ebookToDelete);
      setIsModalOpen(false);
    }
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setEbookToDelete(null);
  };

  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}
      {selectedEbookId && canEdit ? (
        <EditEbookIndex ebookId={selectedEbookId} setSelectedEbookId={setSelectedEbookId} />
      ) : (
        <div className=" space-y-4 mt-4 bg-white p-4 rounded-b-lg  shadow-md ">
          <div className="flex justify-between items-center gap-4 mr-1 mb-4">
            <TextField
              fullWidth
              variant="outlined"
              label="Search"
              placeholder="Search By"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ maxWidth: '400px' }}
            />
            <div className="flex gap-4">
              <Button
                onClick={handleSortByTime}
                className="py-1.5 px-9 rounded"
                children={getSortByTimeButtonText(timeSortOrder)}
                type="button"
              />
              {canAdd && (
                <Button
                  onClick={handleAdd}
                  className="py-1.5 px-9 rounded"
                  children="Add Ebook"
                  type="button"
                />
              )}
            </div>
          </div>
          <CommonDataGrid
            id="ebook"
            rows={ebookData}
            columns={columns}
            loading={loading}
            showConfirmation={false}
            confirmationMessage={""}
            setFilterModel={setFilterModel}
            setSortingModel={setSortingModel}
            //Pagination
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onCancelDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            onConfirmDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            toolbarProps={{ toolbar: { showQuickFilter: true } }}
          />
        </div>
      )}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message={getDeleteConfirmationMessage("ebook")}
      />
      <Toaster />
    </div>
  );
};

export default Ebook;
